import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Slide,
  Typography,
} from "@mui/material";
import { Button, ConfigProvider, QRCode, Tooltip as TooltipAntd } from "antd";
import { TransitionProps } from "@mui/material/transitions";
import {
  DownCircleOutlined,
  LeftCircleOutlined,
  MinusOutlined,
  PlusOutlined,
  PrinterOutlined,
  QrcodeOutlined,
  RightCircleOutlined,
} from "@ant-design/icons";

import { encryptHelper } from "../../../../libs/helpers";
import { useReactToPrint } from "react-to-print";
import html2canvas from "html2canvas";
import logoPng from "../../../../images/logo.svg";
import {
  COLOR_PRIMARY,
  COLOR_SECONDARY,
  LINK_QR,
  STRING_KEY,
} from "../../../../libs/constants";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface DialogPropsI {
  open: boolean;
  onClose: () => void;
  link: string;
  name: string;
  code: string;
}

const MIN_SIZE = 100;
const MAX_SIZE = 400;

interface ButtonQrPropsI {
  item: any;
  disabled?: boolean; // Añadir propiedad para deshabilitar en modo offline
  concatLink:
    | "workflow-empty/"
    | "workflow-full/"
    | "container/"
    | "workflow-checkpoint/";
}

export const ButtonQr = ({ item, concatLink }: ButtonQrPropsI) => {
  const [open, setOpen] = useState(false);
  const [linkQr, setLinkQr] = useState<string>("");
  // const [continuoDialog, setContinuoDialog] = useState(false);

  // useEffect(() => {
  //   if (continuoDialog) {
  //     setContinuoDialog(false);
  //     setOpen(true);
  //   }
  // }, [continuoDialog]);

  const handleGenerateQr = React.useCallback(() => {
    const encrypted = encryptHelper(STRING_KEY + item.id.toString());
    setLinkQr(`${LINK_QR}${concatLink}${encrypted}`);
    setOpen(true);
  }, [item.id, concatLink]);

  return (
    <>
      <TooltipAntd title="Generar QR">
        <Button
          type="dashed"
          size="small"
          onClick={handleGenerateQr}
          icon={<QrcodeOutlined />}
        />
      </TooltipAntd>
      {open && (
        <DialogQr
          open={open}
          onClose={() => setOpen(false)}
          link={linkQr}
          name={item.name}
          code={item.code}
        />
      )}
    </>
  );
};

const DialogQr: React.FC<DialogPropsI> = ({
  open,
  onClose,
  link,
  name,
  code,
}) => {
  const [size, setSize] = useState<number>(200);
  const componentRef = useRef<HTMLDivElement>(null);

  const downloadQRCode = React.useCallback(async () => {
    if (componentRef.current) {
      const canvas = await html2canvas(componentRef.current);
      const link = document.createElement("a");
      link.download = `QRCode_${code}.png`;
      link.href = canvas.toDataURL();
      link.click();
    }
  }, [code]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const adjustSize = (delta: number) => {
    setSize((prevSize) =>
      Math.min(Math.max(prevSize + delta, MIN_SIZE), MAX_SIZE)
    );
  };

  const handleOpenNewTab = () => {
    window.open(link, "_blank", "noopener,noreferrer");
  };

  return (
    <ConfigProvider theme={{ token: { colorPrimary: COLOR_PRIMARY } }}>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={onClose}
        PaperProps={{ sx: { borderRadius: "10px", overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            backgroundColor: COLOR_PRIMARY,
            color: "white",
            py: 1.5,
          }}
        >
          <QrcodeOutlined style={{ fontSize: 22 }} />
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: 15 }}
            >
              Código QR
            </Typography>
            <Typography
              variant="caption"
              sx={{ opacity: 0.8, fontSize: 11, display: "block" }}
            >
              Acceso al registro del contenedor
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#F5F7FA", pt: "24px !important" }}>
          <Grid container spacing={2} justifyContent="center" alignItems="center">
            <Grid item xs={12} sx={{ display: "grid", placeContent: "center" }}>
              <div
                ref={componentRef}
                style={{
                  textAlign: "center",
                  background: "#fff",
                  borderRadius: 8,
                  boxShadow: "0 4px 14px rgba(30, 58, 95, 0.18)",
                  overflow: "hidden",
                  border: `1px solid ${COLOR_PRIMARY}`,
                }}
              >
                <QRCode
                  value={link}
                  bgColor="#fff"
                  size={size}
                  iconSize={size / 4}
                  icon={logoPng}
                  style={{ border: "none", borderRadius: 0 }}
                />
                <Box
                  sx={{
                    backgroundImage: `linear-gradient(to bottom, ${COLOR_PRIMARY}, ${COLOR_SECONDARY})`,
                    width: size + 32,
                    p: 1,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      fontSize: 10,
                      textWrap: "wrap",
                    }}
                  >
                    Neron Plataforma Integral para Contenedores
                  </Typography>
                </Box>
              </div>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{ display: "grid", placeContent: "center", mt: 1 }}
            >
              <Button.Group>
                <Button
                  onClick={() => adjustSize(-10)}
                  disabled={size <= MIN_SIZE}
                  icon={<MinusOutlined />}
                />
                <Button
                  type="primary"
                  onClick={downloadQRCode}
                  icon={<DownCircleOutlined />}
                >
                  Descargar
                </Button>
                <Button
                  type="primary"
                  onClick={handlePrint}
                  icon={<PrinterOutlined />}
                >
                  Imprimir
                </Button>
                <Button
                  type="primary"
                  onClick={handleOpenNewTab}
                  icon={<RightCircleOutlined />}
                >
                  Entrar
                </Button>
                <Button
                  onClick={() => adjustSize(10)}
                  disabled={size >= MAX_SIZE}
                  icon={<PlusOutlined />}
                />
              </Button.Group>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#fff", borderTop: "1px solid #E1E6EE", px: 3, py: 1.5 }}>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </ConfigProvider>
  );
};
